import { Injectable, inject } from "@angular/core";
import { combineLatest, forkJoin, Observable, ReplaySubject } from "rxjs";
import { map, tap } from "rxjs/operators";

import { LgMatTrackingService, LG_USER_INFO } from "@logex/framework/lg-application";
import { SharedAuthorizationApiService } from "./shared-authorization-api.service";
import { SelfServiceEventTracer } from "@mrdm-self-service-frontend/shared/util-tracking";
import { SharedAppUser } from "libs/shared/util-logex-framework-setup/src/lib/shared-app-user";
import {
    AuthorizationPermission,
    AuthorizationProfile
} from "@mrdm-self-service-frontend/shared/types";
import { Language } from "@mrdm-self-service-frontend/shared/assets";
import { toBoolean } from "@logex/framework/utilities";

@Injectable({
    providedIn: "root"
})
export class SharedUserAuthorizationService {
    private _tracer = inject(SelfServiceEventTracer);
    private _matomo = inject(LgMatTrackingService);
    private _authorizationApi = inject(SharedAuthorizationApiService);
    private _userInfo = inject<SharedAppUser>(LG_USER_INFO);

    readonly organizationId$ = new ReplaySubject<number | null>(1);
    readonly userPermissions$ = new ReplaySubject<AuthorizationPermission[]>();
    readonly userProfile$ = new ReplaySubject<AuthorizationProfile>();

    readonly currentOrganizationPermissions$: Observable<AuthorizationPermission[]> = combineLatest(
        [this.organizationId$, this.userPermissions$]
    ).pipe(
        map(([organizationId, permissions]) => {
            return permissions.filter(permision => permision.organizationId === organizationId);
        }),
        tap(authorizationPermissions => {
            this._userInfo.roles = {
                ssp_admin: authorizationPermissions.some(ap =>
                    ap.permissions.some(x => x === "ssp_admin")
                )
            };
        })
    );

    constructor() {
        this.userProfile$.subscribe(userProfile => {
            this._matomo.setUserID(userProfile.email);
        });

        this.organizationId$.subscribe(organizationId => {
            if (organizationId) {
                this._tracer.setOrganization(organizationId);
            }
        });
    }

    loadUserData(): Observable<{
        user: AuthorizationProfile;
        permissions: AuthorizationPermission[];
    }> {
        return forkJoin({
            user: this._getUserProfile(),
            permissions: this._getPermissions()
        });
    }

    setOrganizationId(organizationId: number | null): void {
        this.organizationId$.next(organizationId);
    }

    private _getPermissions(): Observable<AuthorizationPermission[]> {
        return this._authorizationApi
            .searchPermissions()
            .pipe(tap(permissions => this._setPermissions(permissions)));
    }

    private _setPermissions(permissionResponse: AuthorizationPermission[]): void {
        this.userPermissions$.next(permissionResponse);
    }

    private _getUserProfile(): Observable<AuthorizationProfile> {
        return this._authorizationApi
            .getProfile()
            .pipe(tap((user: AuthorizationProfile) => this._setUserInfo(user)));
    }

    private _setUserInfo(user: AuthorizationProfile): void {
        Object.assign(this._userInfo, user);
        this._userInfo.name = `${user.firstName} ${user.lastName}`;
        this._userInfo.locale = user.metadata?.Locale as Language;
        this._userInfo.SSPLegalConsentGranted = toBoolean(user.metadata?.SSPLegalConsentGranted);
        this._userInfo.impersonator = user.impersonation?.originalUserEmail;
        this._userInfo.impersonatorName = user.impersonation?.originalUserName;
        this._userInfo.initialized = true;

        this.userProfile$.next(user);
    }
}
